import {
  afterNextRender,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { NgClass } from '@angular/common';
import { BehaviorSubject, Subscription } from 'rxjs';
export enum DevSiteKey {
  ALWAYS_PASSES = '1x00000000000000000000AA',
  ALWAYS_BLOCKS = '2x00000000000000000000AB',
  ALWAYS_PASSES_INVISIBLE = '1x00000000000000000000BB',
  ALWAYS_BLOCKS_INVISIBLE = '2x00000000000000000000BB',
  FORCE_INTERACTIVE_CHALLENGE = '3x00000000000000000000FF',
}
// we need to declare the `turnstile` object on the window
// so that TypeScript doesn't complain when we try to access it.
declare global {
  interface TurnstileOptions {
    sitekey: string;
    theme?: 'light' | 'dark' | 'auto';
    size?: 'normal' | 'flexible' | 'compact';
    callback?: (token: string) => void;
    'error-callback'?: (errorCode: string) => void;
    'expired-callback'?: () => void;
    'timeout-callback'?: () => void;
  }

  interface Window {
    onloadTurnstileCallback: () => void;
    turnstile: {
      render: (
        container: string | HTMLElement,
        options: TurnstileOptions
      ) => string | undefined;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      ready: (callback: () => void) => void;
    };
  }
}

const SCRIPT_ID = 'AppTurnstileScript';

@Component({
    selector: 'app-turnstile',
    imports: [
        NgClass
    ],
    templateUrl: './turnstile.component.html',
    styleUrl: './turnstile.component.less',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TurnstileComponent),
            multi: true
        }
    ]
})
export class TurnstileComponent implements OnDestroy, ControlValueAccessor {
  private subscription = new Subscription();
  private scriptLoadedSubject = new BehaviorSubject<boolean>(false);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private isDisabled = false;

  token: string | null = null;
  widgetId!: string|null|undefined;
  siteKey = environment.turnstileSiteKey;

  @Input()
  size: 'normal' | 'flexible' | 'compact' = 'normal';

  @Output()
  verified = new EventEmitter<string|null>();

  @Output()
  error = new EventEmitter<string|null>();

  @Output()
  expired = new EventEmitter<string|null>();

  @Output()
  timeout = new EventEmitter<string|null>();

  @ViewChild('container', { static: true }) turnstileContainer!: ElementRef;

  constructor(private zone: NgZone,
              @Inject(DOCUMENT) private document: Document,) {
    this.loadTurnstileScript();

    afterNextRender(() => {
      if (!this.isDisabled && this.scriptLoadedSubject.value && !this.widgetId) {
        this.createWidget();
      }
    });

    this.subscription.add(
      this.scriptLoadedSubject.subscribe((loaded) => {
        if (loaded && !this.widgetId && !this.isDisabled) {
          this.createWidget();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.widgetId) {
      window.turnstile.remove(this.widgetId);
    }
  }

  public reset(markTouched = false): void {
    if (this.widgetId) {
      window.turnstile.reset(this.widgetId);
      this.runInAngularZone(() => {
        this.updateFormValue(null, markTouched);
      });
    }
  }

  private createWidget(): void {
    const turnstileOptions: TurnstileOptions = {
      sitekey: this.siteKey,
      theme: 'auto',
      size: this.size,
      callback: (token: string) => {
        this.runInAngularZone(() => {
          this.updateFormValue(token, true);
          this.verified.emit(token);
        });
      },
      'error-callback': (errorCode: string) => {
        this.runInAngularZone(() => {
          this.updateFormValue(null, true);
          this.error.emit(errorCode);
         });
       },
       'expired-callback': () => {
        this.runInAngularZone(() => {
          this.reset(true);
          this.expired.emit(null);
        });
      },
      'timeout-callback': () => {
        this.runInAngularZone(() => {
          this.reset(true);
          this.timeout.emit(null);
        });
      }
    };

    const render = () => {
      if (this.isDisabled || !this.turnstileContainer) {
        return;
      }

      this.remove();

      this.widgetId = window.turnstile.render(this.turnstileContainer.nativeElement, turnstileOptions);
    };

    if (this.scriptLoadedSubject.value) {
      render();
    }
  }

  private loadTurnstileScript(): void {
    window.onloadTurnstileCallback = () => {
      this.scriptLoadedSubject.next(true);
    };

    this.scriptLoadedSubject.next(!!window.turnstile);
    const scriptAttached = !!this.document.getElementById(SCRIPT_ID);

    if (!this.scriptLoadedSubject.value  && !scriptAttached) {
      const script = this.document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback';
      script.id = SCRIPT_ID;
      script.async = true;
      script.defer = true;
      this.document.head.appendChild(script);
    }
  }

  private remove(): void {
    if (this.widgetId) {
      window.turnstile.remove(this.widgetId);
      this.widgetId = null;
      this.runInAngularZone(() => {
        this.updateFormValue(null);
      });
    }
  }

  writeValue(token: string | null): void {
    this.token = token;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (this.isDisabled) {
      this.remove();
    } else if (this.scriptLoadedSubject.value && !this.widgetId) {
      this.createWidget();
    }
  }

  private updateFormValue(token: string | null, markTouched = false): void {
    this.token = token;
    this.onChange(token);

    if (markTouched) {
      this.onTouched();
    }
  }

  private runInAngularZone(fn: () => void): void {
    if (NgZone.isInAngularZone()) {
      fn();
      return;
    }

    this.zone.run(fn);
  }
}
